import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Truck, ShieldCheck, Loader2, Eye, ShoppingCart, MapPin, MessageCircle,
  Sparkles, PackageCheck, Smartphone, Building2, Mountain, Bike, ArrowRight, Star, Quote,
} from "lucide-react";
import { api } from "../../lib/api";
import { fcfa, PLACEHOLDER_IMG } from "../../lib/format";
import { ShopHeader } from "../../components/shop/ShopHeader";
import { ShopFooter } from "../../components/shop/ShopFooter";
import { useCart } from "../../context/CartContext";
import { toast } from "sonner";

const PURPLE = "#5B2A9D";
const GOLD = "#C9A227";

function Eyebrow({ children }) {
  return (
    <span className="block text-xs font-bold tracking-[0.3em] uppercase" style={{ color: GOLD }}>
      {children}
    </span>
  );
}

function ProductCard({ p, index }) {
  const navigate = useNavigate();
  const { addItem, openCart } = useCart();

  const order = () => {
    addItem(p);
    toast.success(`${p.name} ajouté au panier`);
    openCart();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: index * 0.08 }}
      data-testid={`product-card-${p.id}`}
      className="group bg-white rounded-3xl border border-slate-200/80 overflow-hidden flex flex-col shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
    >
      <div
        className="relative aspect-[4/3] overflow-hidden cursor-pointer"
        style={{ background: "linear-gradient(135deg,#ffffff,#f3f0fb)" }}
        onClick={() => navigate(`/produit/${p.id}`)}
      >
        <img
          src={p.image_url || PLACEHOLDER_IMG}
          alt={p.name}
          className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500"
        />
        <span className="absolute top-3 left-3 text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full" style={{ background: GOLD, color: "#1a0c34" }}>
          NOUMABOU™
        </span>
        <span className={`absolute top-3 right-3 text-[11px] font-bold px-2.5 py-1 rounded-full flex items-center gap-1 ${p.coming_soon ? "text-brand-ink" : p.in_stock ? "bg-green-500 text-white" : "bg-rose-500 text-white"}`} style={p.coming_soon ? { background: GOLD } : undefined}>
          {p.coming_soon ? "Bientôt disponible" : p.in_stock ? "✓ En stock" : "Rupture"}
        </span>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <Eyebrow>{p.category || "Complément naturel"}</Eyebrow>
        <h3 className="font-heading font-bold text-lg text-brand-ink mt-1.5 leading-tight">{p.name}</h3>
        {p.review_count > 0 && (
          <div className="flex items-center gap-1 mt-1.5" data-testid={`rating-${p.id}`}>
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className="w-3.5 h-3.5" style={{ fill: n <= Math.round(p.rating) ? GOLD : "transparent", color: GOLD }} />
            ))}
            <span className="text-xs text-slate-400 ml-1">{p.rating} ({p.review_count})</span>
          </div>
        )}
        <p className="text-sm text-slate-500 mt-2 line-clamp-2 flex-1">{p.description}</p>

        {p.coming_soon ? (
          <p className="mt-4 text-sm font-bold" style={{ color: GOLD }}>Prix communiqué au lancement</p>
        ) : (
          <div className="flex items-baseline gap-1.5 mt-4">
            <span className="font-heading font-black text-2xl" style={{ color: PURPLE }}>{fcfa(p.price).replace(" FCFA", "")}</span>
            <span className="text-xs font-semibold text-slate-400">FCFA</span>
          </div>
        )}

        <div className="mt-4 flex flex-col gap-2">
          <button
            data-testid={`view-product-${p.id}`}
            onClick={() => navigate(`/produit/${p.id}`)}
            className="w-full rounded-full py-2.5 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 border-2 transition-colors"
            style={{ borderColor: PURPLE, color: PURPLE }}
          >
            <Eye className="w-4 h-4" /> Voir le produit
          </button>
          {p.coming_soon ? (
            <button disabled data-testid={`coming-soon-${p.id}`} className="w-full rounded-full py-2.5 text-sm font-bold uppercase tracking-wider text-brand-ink cursor-not-allowed" style={{ background: `${GOLD}33`, border: `2px solid ${GOLD}` }}>
              Bientôt disponible
            </button>
          ) : p.in_stock ? (
            <button
              data-testid={`order-product-${p.id}`}
              onClick={order}
              className="w-full rounded-full py-2.5 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 text-white shadow-md hover:brightness-110 transition-all"
              style={{ background: PURPLE }}
            >
              <ShoppingCart className="w-4 h-4" /> Commander
            </button>
          ) : (
            <button disabled className="w-full rounded-full py-2.5 text-sm font-bold uppercase tracking-wider bg-slate-200 text-slate-400">
              Indisponible
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}

const STEPS = [
  { n: 1, t: "Choisissez", d: "Cliquez sur « Voir le produit » pour voir tous les détails." },
  { n: 2, t: "Commandez", d: "Cliquez sur « Commander » et remplissez le formulaire." },
  { n: 3, t: "Payez", d: "Via Flooz, T-Money ou paiement en ligne sécurisé." },
  { n: 4, t: "Recevez", d: "Un agent vous contacte sur WhatsApp pour la remise de votre commande." },
];

export default function ShopHome() {
  const [products, setProducts] = useState(null);
  const [delivery, setDelivery] = useState(null);
  const [payment, setPayment] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data)).catch(() => setProducts([]));
    api.get("/settings/delivery").then(({ data }) => setDelivery(data)).catch(() => {});
    api.get("/settings/payment").then(({ data }) => setPayment(data)).catch(() => {});
    api.get("/reviews/recent?limit=6").then(({ data }) => setTestimonials(data)).catch(() => {});
    // Capture du code de parrainage partenaire (?ref=CODE)
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("noumabou_ref", ref.toUpperCase());
  }, []);

  const waNumber = (delivery?.whatsapp_number || payment?.flooz_number || "").replace(/\D/g, "");

  return (
    <div className="App overflow-x-hidden bg-white">
      <ShopHeader />

      {/* Hero */}
      <section
        className="relative text-white"
        style={{ background: "radial-gradient(120% 120% at 50% 0%, #4a2585 0%, #26124a 55%, #180a30 100%)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center lg:text-left">
              <p className="text-xs font-bold tracking-[0.3em] uppercase mb-5" style={{ color: GOLD }}>
                🇹🇬 Compléments naturels au Togo
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight">
                Boutique <span style={{ color: GOLD }}>NOUMABOU</span>
              </h1>
              <p className="text-base sm:text-lg text-white/70 mt-5 max-w-xl mx-auto lg:mx-0">
                Compléments alimentaires de qualité. Commandez facilement, un agent vous contacte ensuite sur WhatsApp.
              </p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-3 mt-8">
                {[
                  { i: PackageCheck, t: "Stock disponible" },
                  { i: MessageCircle, t: "Suivi WhatsApp" },
                  { i: ShieldCheck, t: "Paiement sécurisé" },
                ].map((b, k) => (
                  <span key={k} className="inline-flex items-center gap-2 text-sm font-medium rounded-full px-4 py-2 bg-white/10 border border-white/15 backdrop-blur-sm">
                    <b.i className="w-4 h-4" style={{ color: GOLD }} /> {b.t}
                  </span>
                ))}
              </div>
              <a href="#produits" data-testid="hero-shop-cta" className="inline-flex items-center gap-2 mt-9 rounded-full px-8 py-3.5 font-bold text-brand-ink shadow-lg hover:brightness-105 transition-all" style={{ background: GOLD }}>
                <Sparkles className="w-5 h-5" /> Voir les produits
              </a>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}>
              <div className="rounded-3xl overflow-hidden shadow-2xl ring-1 ring-white/10">
                <img src="/products/noumabou-gamme.jpg" alt="La gamme NOUMABOU — La santé au naturel" className="w-full h-full object-cover" />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Payment strip */}
        {payment && (
          <div className="bg-black/25 border-t border-white/10">
            <div className="max-w-5xl mx-auto px-4 py-4 flex flex-wrap items-center justify-center gap-3">
              <span className="text-xs font-bold tracking-widest uppercase text-white/50 mr-1">Paiement :</span>
              <span className="inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-1.5 text-white" style={{ background: "#D97A45" }}>
                <Smartphone className="w-4 h-4" /> Flooz · {payment.flooz_number}
              </span>
              <span className="inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-1.5 text-white" style={{ background: "#2E8B57" }}>
                <Smartphone className="w-4 h-4" /> T-Money · {payment.tmoney_number}
              </span>
              <span className="inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-1.5" style={{ background: GOLD, color: "#1a0c34" }}>
                💵 Paiement en espèces
              </span>
            </div>
          </div>
        )}
      </section>

      {/* Nos Boutiques */}
      <section id="boutiques" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20">
        <div className="text-center">
          <Eyebrow>Nos boutiques</Eyebrow>
          <h2 className="font-heading text-3xl sm:text-4xl font-black text-brand-ink mt-2">
            Choisissez votre <span className="italic" style={{ color: PURPLE }}>pays</span>
          </h2>
          <p className="text-slate-500 mt-3">Commandez dans votre pays. D'autres boutiques arrivent bientôt.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
          <div data-testid="boutique-togo-card" className="rounded-3xl border-2 shadow-md p-8 text-center flex flex-col" style={{ borderColor: PURPLE, background: `${PURPLE}08` }}>
            <span className="text-5xl">🇹🇬</span>
            <h3 className="font-heading text-2xl font-bold text-brand-ink mt-3">Boutique Togo</h3>
            <span className="inline-block mx-auto mt-2 text-[11px] font-bold px-3 py-1 rounded-full bg-green-100 text-green-700">✓ Disponible</span>
            <p className="text-sm text-slate-500 mt-3 flex-1">Flooz · T-Money · Paiement en ligne sécurisé.</p>
            <a href="#produits" data-testid="go-produits-togo" className="mt-6 inline-flex items-center justify-center gap-2 rounded-full py-3 font-bold text-white shadow-md hover:brightness-110 transition-all" style={{ background: PURPLE }}>
              Voir les produits <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          {[{ f: "🇧🇯", n: "Bénin" }, { f: "🇨🇮", n: "Côte d'Ivoire" }].map((c) => (
            <div key={c.n} className="rounded-3xl border-2 border-dashed border-slate-200 p-8 text-center flex flex-col bg-slate-50/60">
              <span className="text-5xl grayscale opacity-60">{c.f}</span>
              <h3 className="font-heading text-2xl font-bold text-slate-400 mt-3">Boutique {c.n}</h3>
              <span className="inline-block mx-auto mt-2 text-[11px] font-bold px-3 py-1 rounded-full bg-amber-100 text-amber-700">Bientôt</span>
              <p className="text-sm text-slate-400 mt-3 flex-1">Compléments NOUMABOU™ bientôt disponibles dans votre pays.</p>
              <button disabled className="mt-6 rounded-full py-3 font-bold text-slate-400 bg-slate-200 cursor-not-allowed">
                Bientôt disponible
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Products */}
      <section id="produits" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="text-center mb-12">
          <Eyebrow>Nos produits</Eyebrow>
          <h2 className="font-heading text-3xl sm:text-4xl font-black text-brand-ink mt-2">
            Compléments <span className="italic" style={{ color: PURPLE }}>Alimentaires</span>
          </h2>
          <p className="text-slate-500 mt-3">Cliquez sur un produit pour voir tous les détails, photos et vidéos.</p>
        </div>

        {products === null ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin" style={{ color: PURPLE }} /></div>
        ) : products.length === 0 ? (
          <p className="text-slate-500 py-10 text-center">Aucun produit disponible pour le moment.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8" data-testid="product-grid">
            {products.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
          </div>
        )}
      </section>

      {/* How to order */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="text-center mb-14">
          <Eyebrow>Comment commander</Eyebrow>
          <h2 className="font-heading text-3xl sm:text-4xl font-black text-brand-ink mt-2">
            Simple en <span className="italic" style={{ color: PURPLE }}>4 étapes</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="text-center"
            >
              <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center font-heading font-black text-xl text-white shadow-lg" style={{ background: PURPLE }}>
                {s.n}
              </div>
              <h4 className="font-heading font-bold text-brand-ink mt-4">{s.t}</h4>
              <p className="text-sm text-slate-500 mt-2">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="bg-brand-cream py-16 sm:py-20" data-testid="testimonials-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <Eyebrow>Avis clients</Eyebrow>
              <h2 className="font-heading text-3xl sm:text-4xl font-black text-brand-ink mt-2">
                Ils nous font <span className="italic" style={{ color: PURPLE }}>confiance</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
                  data-testid={`testimonial-${t.id}`}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200/70 flex flex-col"
                >
                  <Quote className="w-7 h-7 mb-3" style={{ color: GOLD }} />
                  <div className="flex items-center gap-0.5 mb-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className="w-4 h-4" style={{ fill: n <= t.rating ? GOLD : "transparent", color: GOLD }} />
                    ))}
                  </div>
                  <p className="text-sm text-slate-600 flex-1 italic">"{t.comment}"</p>
                  <div className="mt-4 pt-3 border-t border-slate-100">
                    <p className="font-heading font-bold text-brand-ink text-sm">{t.name}</p>
                    {t.product_name && <p className="text-xs text-slate-400">à propos de {t.product_name}</p>}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      <ShopFooter />
    </div>
  );
}
